import { useId, useState, type FC, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Icon } from '@/components/icons';
import { AUTH_PASSWORD_INPUT_CLASS } from '@/styles/authInputClasses';
import { COMMON_CLASSES } from '@/styles/classes';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
  /** Branded auth pages (login, reset): taller fields matching AuthBrandedLayout. */
  inputStyle?: 'default' | 'auth';
}

export const Input: FC<InputProps> = ({
  label,
  error,
  helperText,
  className = '',
  containerClassName,
  id,
  type,
  onInvalid,
  inputStyle = 'default',
  ...props
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = id || generatedId;
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';
  const isAuthPassword = inputStyle === 'auth' && isPasswordField;
  const inputType = isPasswordField && showPassword ? 'text' : type;

  return (
    <div className={clsx('w-full', containerClassName)}>
      {label ? (
        <label htmlFor={inputId} className={COMMON_CLASSES.label}>
          {label}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={inputId}
          type={inputType}
          className={clsx(
            isAuthPassword ? AUTH_PASSWORD_INPUT_CLASS : COMMON_CLASSES.input,
            !isAuthPassword && error && COMMON_CLASSES.inputError,
            !isAuthPassword && isPasswordField && 'auth-password-input-no-reveal pr-12',
            className
          )}
          {...props}
          onInvalid={event => {
            event.preventDefault();
            onInvalid?.(event);
          }}
        />
        {isPasswordField ? (
          <button
            type="button"
            onClick={() => setShowPassword(current => !current)}
            className={clsx(
              'absolute top-1/2 -translate-y-1/2 text-unilink-text-secondary',
              isAuthPassword ? 'right-4' : 'right-3'
            )}
            aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            tabIndex={-1}
          >
            {showPassword ? (
              <Icon icon="eye" className="size-6" />
            ) : (
              <Icon icon="eye-slash" className="size-6" />
            )}
          </button>
        ) : null}
      </div>
      {error ? <p className={`mt-1 ${COMMON_CLASSES.textError}`}>{error}</p> : null}
      {helperText && !error ? (
        <p className={`mt-1 ${COMMON_CLASSES.textHelper}`}>{helperText}</p>
      ) : null}
    </div>
  );
};
